// Disposition constants based on workflow type

// Define workflow types
export enum WorkflowType {
  MEDBV = 'medbv',
  PBM = 'pbm',
  RECALL_SCHEDULER = 'recall_scheduler',
  REFERRAL_SCHEDULER = 'referral_scheduler',
  BENEFIT_VERIFICATION = 'benefit_verification',
  PQC = 'pqc',
  MEDICARE_HUMAN = 'medicare_human',
  UNIFIEDMEDBV = 'unifiedmedbv',
  // BHO (BV-2171): fork of unifiedmedbv with its own "bho" skill (BV-2476).
  BHO = 'bho',
  // MEDBHOBV (BV-2328): human-only clone of bho with its own schema; uses the MedBV disposition list.
  MEDBHOBV = 'medbhobv',
  TRANSPORTATION = 'transportation',
  PA_STATUS_PBM = 'pa_status_pbm',
  PA_STATUS_MEDBV = 'pa_status_medbv',
  REFILL = 'refill',
}

// Backend workflow type for the AE/PQC intake ("preprocessing") workflow that PQC
// cases are created against before transitioning to the PQC workflow. Kept as a
// standalone constant rather than a `WorkflowType` enum member on purpose: the enum
// feeds the user-facing skills picker (`getAvailableSkillOptions`) and disposition
// mapping, and this internal workflow must not surface there. Used only to resolve
// the create-case (intake) schema for the Tier-2 org-wide export.
export const AEPQC_PREPROCESSING_WORKFLOW_TYPE = 'aepqc_preprocessing';

// PQC workflow execution variants
export enum PQCWorkflowVariant {
  EMAIL = 'email',
  PORTAL = 'portal',
  // Record-only: a Tier-1 agent captures a short form and the case auto-completes
  // with no outbound submission (see AQ-357 warm transfer).
  WARM_TRANSFER = 'warm_transfer',
}

// Human-readable label for a PQC submission method (workflow variant). Empty/unknown
// variants default to Email, matching the backend default.
export function getSubmissionMethodLabel(variant?: string): string {
  switch (variant) {
    case PQCWorkflowVariant.PORTAL:
      return 'Portal';
    case PQCWorkflowVariant.WARM_TRANSFER:
      return 'Warm Transfer';
    default:
      return 'Email';
  }
}

// True when a workflow variant is the record-only warm-transfer method.
export function isWarmTransferVariant(variant?: string): boolean {
  return variant === PQCWorkflowVariant.WARM_TRANSFER;
}

// Mercalis dispositions
export const END_CALL_DISPOSITION_BENEFITS_VERIFIED = 'Benefits Verified';
export const END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED = 'Benefits Not Verified';
export const END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK = 'Third Party Pushback';
export const END_CALL_DISPOSITION_PLAN_NOT_ACTIVE = 'Plan Not Active';
export const END_CALL_DISPOSITION_NO_MEMBER_ID = 'Member ID Does Not Exist';
export const END_CALL_DISPOSITION_NO_NPI = 'NPI Number Required';
export const END_CALL_DISPOSITION_NON_WORKING_HOURS = 'Non-Working Hours';
export const END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN = 'Needs a live human';
export const END_CALL_DISPOSITION_MEMBER_NOT_FOUND = 'Member Not Found';
export const END_CALL_DISPOSITION_DRUG_NAME_ISSUE = 'Drug Name Issue';
export const END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED = 'Member Phone Number Required';
export const END_CALL_DISPOSITION_MAINTENANCE_UPDATE = 'System is under maintenance';
export const END_CALL_DISPOSITION_PARTIAL_BENEFITS_VERIFIED = 'Benefits Partially Verified';
export const END_CALL_DISPOSITION_MEMBER_INFO_MISMATCH = 'Member Info Mismatch';

// FoodSmart dispositions
export const END_CALL_DISPOSITION_APPOINTMENT_BOOKED = 'Appointment Booked';
export const END_CALL_DISPOSITION_DO_NOT_CALL = 'Do Not Call';
export const END_CALL_DISPOSITION_VOICE_MAIL = 'Voice Mail';
export const END_CALL_DISPOSITION_SPOKE_TO_MEMBER = 'Spoke to Member';
export const END_CALL_DISPOSITION_CALLBACK_REQUESTED = 'Callback Requested';
export const END_CALL_DISPOSITION_NO_RESPONSE = 'No Response';
export const END_CALL_DISPOSITION_NO_AI = 'Does Not Want to Talk to AI';
export const END_CALL_DISPOSITION_INVALID_TO_PHONE_NO = 'Invalid Phone Number';

// Codex dispositions
export const END_CALL_DISPOSITION_COLLECTED_INFO = 'Collected Information';
export const END_CALL_DISPOSITION_NO_INFORMATION_COLLECTED = 'No Information Collected';
export const END_CALL_DISPOSITION_NOT_AVAILABLE = 'Not Available';

// MedConverge dispositions
export const END_CALL_DISPOSITION_ALL_BENEFITS_VERIFIED = 'All Benefits Verified';

// GastroOne dispositions
export const END_CALL_DISPOSITION_SUCCESSFUL_CALL = 'Successful Call';
export const END_CALL_DISPOSITION_PARTIAL_SUCCESS = 'Partial Success';
export const END_CALL_DISPOSITION_VERIFICATION_FAILED = 'Verification Failed';
export const END_CALL_DISPOSITION_VOICEMAIL_ONLY = 'Voicemail Only';
export const END_CALL_DISPOSITION_ESCALATION_NEEDED = 'Escalation Needed';
export const END_CALL_DISPOSITION_DISQUALIFIED = 'Disqualified';
export const END_CALL_DISPOSITION_UNSUPPORTED_LANGUAGE = 'Unsupported Language';
export const END_CALL_DISPOSITION_RESCHEDULE_OUTREACH_CALL = 'Reschedule Outreach Call';
export const END_CALL_DISPOSITION_WRONG_NUMBER = 'Wrong Number';
export const END_CALL_DISPOSITION_PATIENT_UNAVAILABLE = 'Patient Unavailable';
export const END_CALL_DISPOSITION_APPOINTMENT_ALREADY_BOOKED = 'Appointment Already Booked';
export const END_CALL_DISPOSITION_PATIENT_NOT_VERIFIED = 'Patient Not Verified';
export const END_CALL_DISPOSITION_PATIENT_NOT_INTERESTED = 'Patient Not Interested';
export const END_CALL_DISPOSITION_PATIENT_HUNG_UP = 'Patient Hung Up';

//autonomy dispositions
export const END_CALL_DISPOSITION_DRUG_BENEFITS_VERIFIED = 'Drug Benefits Verified';
export const END_CALL_DISPOSITION_PROCEDURE_BENEFITS_VERIFIED = 'Procedure Benefits Verified';

// Scheduler dispositions (recall & referral)
export const END_CALL_DISPOSITION_SCHEDULED_PROCEDURE = 'Connected - Scheduled for Procedure';
export const END_CALL_DISPOSITION_SCHEDULED_OFFICE_VISIT = 'Connected - Scheduled for Office Visit';
export const END_CALL_DISPOSITION_DETAILS_COLLECTED_NOT_SCHEDULED =
  'Connected - Details collected but not scheduled';
export const END_CALL_DISPOSITION_CONNECTED_NEEDS_LIVE_HUMAN = 'Connected - Needs live human';
export const END_CALL_DISPOSITION_IDENTITY_VERIFICATION_FAILED =
  'Connected - Identity verification failed';
export const END_CALL_DISPOSITION_VOICEMAIL_LEFT = 'Not Connected - Voicemail left';
export const END_CALL_DISPOSITION_CONNECTED_LANGUAGE_NOT_SUPPORTED =
  'Connected - Language not supported';
export const END_CALL_DISPOSITION_CONNECTED_CALLBACK_REQUESTED = 'Connected - Call back requested';
export const END_CALL_DISPOSITION_CONNECTED_WRONG_NUMBER = 'Connected - Wrong phone number';
export const END_CALL_DISPOSITION_CONNECTED_DO_NOT_CALL = 'Connected - Do Not Call';
export const END_CALL_DISPOSITION_REFUSED_AI = 'Connected - Refused to speak to AI';
export const END_CALL_DISPOSITION_CONNECTED_PATIENT_UNAVAILABLE = 'Connected - Patient unavailable';
export const END_CALL_DISPOSITION_CONNECTED_APPOINTMENT_ALREADY_SCHEDULED =
  'Connected - Appointment already scheduled';
export const END_CALL_DISPOSITION_CONNECTED_NOT_INTERESTED = 'Connected - Not interested';
export const END_CALL_DISPOSITION_CONNECTED_PATIENT_HUNG_UP = 'Connected - Patient Hung Up';
export const END_CALL_DISPOSITION_PATIENT_DECEASED = 'Connected - Patient Deceased';
export const END_CALL_DISPOSITION_INVALID_NUMBER = 'Not Connected - Invalid Number';
export const END_CALL_DISPOSITION_SCHEDULED_OFFICE_VISIT_ALL_CONFIRMED =
  'Connected - Scheduled Office Visit, All Details Confirmed';
export const END_CALL_DISPOSITION_SCHEDULED_OFFICE_VISIT_NOT_CONFIRMED =
  'Connected - Scheduled Office Visit, Details Not Confirmed';
export const END_CALL_DISPOSITION_NOT_SCHEDULED_OFFICE_VISIT_ALL_CONFIRMED =
  'Connected - Not Scheduled Office Visit, All Details Confirmed';
export const END_CALL_DISPOSITION_NOT_SCHEDULED_OFFICE_VISIT_NOT_CONFIRMED =
  'Connected - Not Scheduled Office Visit, Details Not Confirmed';
export const END_CALL_DISPOSITION_NOT_SCHEDULED_OFFICE_VISIT_BOOKING_INELIGIBLE =
  'Connected - Not Scheduled Office Visit, Booking Ineligible';
export const END_CALL_DISPOSITION_SCHEDULED_PROCEDURE_ALL_CONFIRMED =
  'Connected - Scheduled Procedure, All Details Confirmed';
export const END_CALL_DISPOSITION_SCHEDULED_PROCEDURE_NOT_CONFIRMED =
  'Connected - Scheduled Procedure, Details Not Confirmed';
export const END_CALL_DISPOSITION_SCHEDULED_PROCEDURE_BOOKING_INELIGIBLE =
  'Connected - Scheduled Procedure, Booking Ineligible';
export const END_CALL_DISPOSITION_NOT_SCHEDULED_PROCEDURE_ALL_CONFIRMED =
  'Connected - Not Scheduled Procedure, All Details Confirmed';
export const END_CALL_DISPOSITION_NOT_SCHEDULED_PROCEDURE_NOT_CONFIRMED =
  'Connected - Not Scheduled Procedure, Details Not Confirmed';
export const END_CALL_DISPOSITION_TECHNICAL_ISSUE = 'Technical Issue';

// Common dispositions
export const END_CALL_DISPOSITION_OTHER = 'Other';
export const END_CALL_DISPOSITION_NO_ANSWER = 'No Answer';
export const END_CALL_DISPOSITION_VOICEMAIL = 'Voicemail';

// Special fields
export const END_CALL_EVALUATION_SCORE = 'evaluation_score';
export const END_CALL_VIOLATION_TYPES = 'policy_violation_types';

// Main configuration mapping workflow types to dispositions
export const WORKFLOW_DISPOSITIONS: Record<string, string[]> = {
  // Mercalis types (medbv, pbm, benefitverification)
  [WorkflowType.MEDBV]: [
    END_CALL_DISPOSITION_ALL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_PROCEDURE_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
    END_CALL_DISPOSITION_OTHER,
  ],
  [WorkflowType.PBM]: [
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_NAME_ISSUE,
    END_CALL_DISPOSITION_MEMBER_INFO_MISMATCH,
    END_CALL_DISPOSITION_MEMBER_NOT_FOUND,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PARTIAL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
    END_CALL_DISPOSITION_OTHER,
  ],
  [WorkflowType.BENEFIT_VERIFICATION]: [
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_NAME_ISSUE,
    END_CALL_DISPOSITION_MEMBER_INFO_MISMATCH,
    END_CALL_DISPOSITION_MEMBER_NOT_FOUND,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PARTIAL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
    END_CALL_DISPOSITION_OTHER,
  ],
  // Recall & Referral scheduler dispositions must stay in sync with
  // migrations/seed_dispositions_v1.go (codes 13000-13028 and 13500-13528).
  [WorkflowType.RECALL_SCHEDULER]: [
    END_CALL_DISPOSITION_SCHEDULED_PROCEDURE,
    END_CALL_DISPOSITION_SCHEDULED_OFFICE_VISIT,
    END_CALL_DISPOSITION_DETAILS_COLLECTED_NOT_SCHEDULED,
    END_CALL_DISPOSITION_CONNECTED_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_IDENTITY_VERIFICATION_FAILED,
    END_CALL_DISPOSITION_VOICEMAIL_LEFT,
    END_CALL_DISPOSITION_CONNECTED_LANGUAGE_NOT_SUPPORTED,
    END_CALL_DISPOSITION_CONNECTED_CALLBACK_REQUESTED,
    END_CALL_DISPOSITION_CONNECTED_WRONG_NUMBER,
    END_CALL_DISPOSITION_CONNECTED_DO_NOT_CALL,
    END_CALL_DISPOSITION_REFUSED_AI,
    END_CALL_DISPOSITION_CONNECTED_PATIENT_UNAVAILABLE,
    END_CALL_DISPOSITION_CONNECTED_APPOINTMENT_ALREADY_SCHEDULED,
    END_CALL_DISPOSITION_CONNECTED_NOT_INTERESTED,
    END_CALL_DISPOSITION_CONNECTED_PATIENT_HUNG_UP,
    END_CALL_DISPOSITION_OTHER,
    END_CALL_DISPOSITION_PATIENT_DECEASED,
    END_CALL_DISPOSITION_INVALID_NUMBER,
    END_CALL_DISPOSITION_SCHEDULED_OFFICE_VISIT_ALL_CONFIRMED,
    END_CALL_DISPOSITION_SCHEDULED_OFFICE_VISIT_NOT_CONFIRMED,
    END_CALL_DISPOSITION_NOT_SCHEDULED_OFFICE_VISIT_ALL_CONFIRMED,
    END_CALL_DISPOSITION_NOT_SCHEDULED_OFFICE_VISIT_NOT_CONFIRMED,
    END_CALL_DISPOSITION_NOT_SCHEDULED_OFFICE_VISIT_BOOKING_INELIGIBLE,
    END_CALL_DISPOSITION_SCHEDULED_PROCEDURE_ALL_CONFIRMED,
    END_CALL_DISPOSITION_SCHEDULED_PROCEDURE_NOT_CONFIRMED,
    END_CALL_DISPOSITION_SCHEDULED_PROCEDURE_BOOKING_INELIGIBLE,
    END_CALL_DISPOSITION_NOT_SCHEDULED_PROCEDURE_ALL_CONFIRMED,
    END_CALL_DISPOSITION_NOT_SCHEDULED_PROCEDURE_NOT_CONFIRMED,
    END_CALL_DISPOSITION_TECHNICAL_ISSUE,
  ],
  [WorkflowType.REFERRAL_SCHEDULER]: [
    END_CALL_DISPOSITION_SCHEDULED_PROCEDURE,
    END_CALL_DISPOSITION_SCHEDULED_OFFICE_VISIT,
    END_CALL_DISPOSITION_DETAILS_COLLECTED_NOT_SCHEDULED,
    END_CALL_DISPOSITION_CONNECTED_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_IDENTITY_VERIFICATION_FAILED,
    END_CALL_DISPOSITION_VOICEMAIL_LEFT,
    END_CALL_DISPOSITION_CONNECTED_LANGUAGE_NOT_SUPPORTED,
    END_CALL_DISPOSITION_CONNECTED_CALLBACK_REQUESTED,
    END_CALL_DISPOSITION_CONNECTED_WRONG_NUMBER,
    END_CALL_DISPOSITION_CONNECTED_DO_NOT_CALL,
    END_CALL_DISPOSITION_REFUSED_AI,
    END_CALL_DISPOSITION_CONNECTED_PATIENT_UNAVAILABLE,
    END_CALL_DISPOSITION_CONNECTED_APPOINTMENT_ALREADY_SCHEDULED,
    END_CALL_DISPOSITION_CONNECTED_NOT_INTERESTED,
    END_CALL_DISPOSITION_CONNECTED_PATIENT_HUNG_UP,
    END_CALL_DISPOSITION_OTHER,
    END_CALL_DISPOSITION_PATIENT_DECEASED,
    END_CALL_DISPOSITION_INVALID_NUMBER,
    END_CALL_DISPOSITION_SCHEDULED_OFFICE_VISIT_ALL_CONFIRMED,
    END_CALL_DISPOSITION_SCHEDULED_OFFICE_VISIT_NOT_CONFIRMED,
    END_CALL_DISPOSITION_NOT_SCHEDULED_OFFICE_VISIT_ALL_CONFIRMED,
    END_CALL_DISPOSITION_NOT_SCHEDULED_OFFICE_VISIT_NOT_CONFIRMED,
    END_CALL_DISPOSITION_NOT_SCHEDULED_OFFICE_VISIT_BOOKING_INELIGIBLE,
    END_CALL_DISPOSITION_SCHEDULED_PROCEDURE_ALL_CONFIRMED,
    END_CALL_DISPOSITION_SCHEDULED_PROCEDURE_NOT_CONFIRMED,
    END_CALL_DISPOSITION_SCHEDULED_PROCEDURE_BOOKING_INELIGIBLE,
    END_CALL_DISPOSITION_NOT_SCHEDULED_PROCEDURE_ALL_CONFIRMED,
    END_CALL_DISPOSITION_NOT_SCHEDULED_PROCEDURE_NOT_CONFIRMED,
    END_CALL_DISPOSITION_TECHNICAL_ISSUE,
  ],
  [WorkflowType.MEDICARE_HUMAN]: [
    END_CALL_DISPOSITION_ALL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_PROCEDURE_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
    END_CALL_DISPOSITION_OTHER,
  ],
  [WorkflowType.UNIFIEDMEDBV]: [
    END_CALL_DISPOSITION_ALL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    // 'Other' deliberately absent: the engine catalog retired 1_002_000 to
    // 'Other (deprecated)' and it is not selectable. This label-based list is
    // only reached when the engine returns nothing for the workflow, which it
    // never does for unifiedmedbv — kept consistent so the two cannot disagree.
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_PROCEDURE_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
  ],
  // BHO (BV-2171) reuses the unifiedmedbv disposition catalog (engine aliases
  // bho -> unifiedmedbv); this label-based fallback mirrors it for parity.
  [WorkflowType.BHO]: [
    END_CALL_DISPOSITION_ALL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_PROCEDURE_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
  ],
  // MEDBHOBV (BV-2328) uses the MedBV (unifiedmedbv) disposition list (the engine
  // aliases medbhobv -> unifiedmedbv); this label-based fallback mirrors it.
  [WorkflowType.MEDBHOBV]: [
    END_CALL_DISPOSITION_ALL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_PROCEDURE_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
  ],
  [WorkflowType.TRANSPORTATION]: [
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_NAME_ISSUE,
    END_CALL_DISPOSITION_MEMBER_INFO_MISMATCH,
    END_CALL_DISPOSITION_MEMBER_NOT_FOUND,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PARTIAL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
    END_CALL_DISPOSITION_OTHER,
  ],
  // TODO(BI-1447): These pa_status_pbm / pa_status_medbv end-call disposition lists
  // are hand-maintained copies of unifiedmedbv's, powering the manual end-call
  // dropdowns (GlobalDialpad / ActionCallReviewDialog). The engine now has native
  // handlers for both workflows (AA-964) with their own catalogs, so these lists
  // should be sourced from the engine catalog rather than kept by hand — left as
  // a follow-up so this change stays limited to re-enabling the engine path.
  [WorkflowType.PA_STATUS_PBM]: [
    END_CALL_DISPOSITION_ALL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    END_CALL_DISPOSITION_OTHER,
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_PROCEDURE_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
  ],
  [WorkflowType.PA_STATUS_MEDBV]: [
    END_CALL_DISPOSITION_ALL_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_NOT_VERIFIED,
    END_CALL_DISPOSITION_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_DRUG_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_MAINTENANCE_UPDATE,
    END_CALL_DISPOSITION_NEEDS_LIVE_HUMAN,
    END_CALL_DISPOSITION_NO_MEMBER_ID,
    END_CALL_DISPOSITION_NO_NPI,
    END_CALL_DISPOSITION_NON_WORKING_HOURS,
    END_CALL_DISPOSITION_OTHER,
    END_CALL_DISPOSITION_PHONE_NUMBER_REQUIRED,
    END_CALL_DISPOSITION_PLAN_NOT_ACTIVE,
    END_CALL_DISPOSITION_PROCEDURE_BENEFITS_VERIFIED,
    END_CALL_DISPOSITION_THIRD_PARTY_PUSHBACK,
  ],
};

/**
 * Workflow types that support case-level dispositions.
 * Must stay in sync with TableBuilder's getStandardColumns logic.
 */
// pa_status_pbm / pa_status_medbv were excluded under BI-1447 because they had
// no disposition-engine handler and aliasing them onto unifiedmedbv produced
// inaccurate missed/required fields. The engine now ships handlers of their own
// (AA-964, with their own code ranges 1_007_xxx / 1_008_xxx), so they compute
// natively and are included here.
export const DISPOSITION_WORKFLOW_TYPES: string[] = [
  WorkflowType.PBM,
  WorkflowType.UNIFIEDMEDBV,
  WorkflowType.BHO,
  WorkflowType.MEDBHOBV,
  WorkflowType.TRANSPORTATION,
  WorkflowType.PA_STATUS_PBM,
  WorkflowType.PA_STATUS_MEDBV,
  WorkflowType.REFILL,
];

// Default dispositions for workflows without a specific type
export const DEFAULT_DISPOSITIONS: string[] = [
  END_CALL_DISPOSITION_NO_ANSWER,
  END_CALL_DISPOSITION_VOICEMAIL,
  END_CALL_DISPOSITION_WRONG_NUMBER,
  END_CALL_DISPOSITION_CALLBACK_REQUESTED,
  END_CALL_DISPOSITION_OTHER,
];

// The disposition-related boolean flags in output_data are no longer listed
// here: the reset list is derived per workflow from the disposition engine —
// see src/lib/disposition/resetFlags.ts (BV-1877).
